/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-empty-function */

'use client';

import { useState, useEffect, useMemo, useRef } from 'react';
import dynamic from 'next/dynamic';
import { usePathname, useSearchParams } from 'next/navigation';
import remoteConfigDiscovery from 'json/RemoteConfig.json';
import { OptionsFilters, RemoteConfig } from 'RemoteConfig';
import { useInView } from 'react-intersection-observer';

import { createPropsToTrackingMatrix } from 'components/Integration/createPropsToTrackingMatrix';
import { Dropdown, IconArrowDown } from '@sympla/sympla-components';

import searchCategoryType from 'services/searchCategoryType/searchClient';
import { createLinkToRedirect } from 'utils/createLinkToRedirect';
import addEventToDataLayer from 'components/Integration/addEventToDataLayer';
import { CityCarouselSkeleton } from 'components/CityCarousel/CityCarousel';
import { PAGE, FILTER_CAROUSEL } from 'components/Integration/constantsLabel';
import { cardVariants } from 'constants/search';
import {
  analytics,
  AnalyticsCardData,
  analyticsEvents,
  getParamsRailClicked,
  transportMethods
} from 'lib/analytics';

import {
  CardOptions,
  CardPreviousView,
  CollectionData,
  CollectionMatrix,
  MatrixComponent
} from 'matrix.d';
import { Location } from 'location';
import { CarouselWrapperSkeleton } from './CarouselWrapper.skeleton';

import { cardOptions } from './cardOptions';
import { sendTopsortEvent } from 'services/topsort/sendTopsortEvent';
import { getCookie } from 'cookies-next';
import { browserLogger } from 'utils/logger/browserLogger';
import { getCardPreviousView } from 'utils/getCardPreviousView';
import * as styles from './CarouselWrapper.css';

type AdsContext = {
  bidId: string;
  landingPath: string;
};

const IconArrowLeft = dynamic<IconProps>(
  () =>
    import('@sympla/sympla-components/NavigationIcons').then(
      (mod) => mod.IconArrowLeft
    ),
  {
    ssr: false
  }
);
const IconArrowRight = dynamic<IconProps>(
  () =>
    import('@sympla/sympla-components/NavigationIcons').then(
      (mod) => mod.IconArrowRight
    ),
  {
    ssr: false
  }
);
const Carousel = dynamic(
  () => import('@sympla/sympla-components/HomeCarousel'),
  { ssr: false }
);

const CityCarousel = dynamic(
  () =>
    import('components/CityCarousel/CityCarousel').then(
      (mod) => mod.CityCarousel
    ),
  { ssr: false }
);
const Button = dynamic(() => import('@sympla/sympla-components/Button'), {
  ssr: false
});
const ShowMoreCard = dynamic(
  () => import('components/Shared/ShowMoreCard/ShowMoreCard'),
  { ssr: false }
);

interface CarouselWrapperProps {
  component: MatrixComponent;
  userLocation: Location | null;
  prefixTrackingName: string;
  matrixUuid: string;
  matrixType: string;
  cardPreviousView: CardPreviousView;
  specialUUIDs?: string[];
  hideTitle?: boolean;
  isCollectionSearchVariant?: boolean;
  pageTitleForTracking?: string;
}

export function CarouselWrapper({
  component,
  userLocation,
  prefixTrackingName,
  matrixUuid,
  matrixType,
  cardPreviousView,
  pageTitleForTracking,
  specialUUIDs = [],
  hideTitle = false,
  isCollectionSearchVariant = false
}: CarouselWrapperProps) {
  const { ref, inView } = useInView();
  const [isMounted, setIsMounted] = useState(false);
  const [controls, setControls] = useState({
    next: () => {},
    prev: () => {},
    slidesTotal: 0
  });
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const authContainerRef = useRef<HTMLDivElement>(null);

  const logger = browserLogger();

  const [btnArrows, setBtnArrows] = useState({
    beginning: true,
    end: false
  });

  const [isLoading, setIsLoading] = useState(false);

  const responseData =
    component?.response && cardOptions[component.response_type]
      ? component.response[cardOptions[component.response_type]!.nameData] || []
      : [];

  const [CardComponentsDataList, setCardComponentsDataList] = useState<
    CollectionData[] | CollectionMatrix[]
  >(responseData);

  useEffect(() => {
    if (responseData.length > 0 && CardComponentsDataList.length === 0) {
      setCardComponentsDataList(responseData);
    }
  }, [responseData, CardComponentsDataList.length]);

  const hasData = CardComponentsDataList.length > 0;

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const filterMatrix = (remoteConfigDiscovery as RemoteConfig).discovery
    ?.matrices?.[component?.uuid]?.filters;
  const [filterCarousel, setFilterCarousel] = useState(
    filterMatrix?.[0] || { title: '', range: '', uuid: '' }
  );

  const accountId = searchParams?.get('account_id');
  const timestamp = searchParams?.get('timestamp');
  const hash = searchParams?.get('hash');
  const utmSource = searchParams?.get('utm_source');
  const utmCampaign = searchParams?.get('utm_campaign');
  const discountCode = searchParams?.get('d') as string | undefined;

  const adsContextCookie = getCookie('ts_ads_context');
  let adsContext: AdsContext | null = null;

  if (typeof adsContextCookie === 'string' && adsContextCookie) {
    try {
      const parsedAdsContext = JSON.parse(adsContextCookie) as AdsContext;
      if (parsedAdsContext?.bidId && parsedAdsContext?.landingPath) {
        adsContext = parsedAdsContext;
      }
    } catch (error) {
      logger.error(
        {
          message:
            'Unable to get cookie data for Topsort click event or send event',
          data: JSON.stringify({
            error: {
              message: error instanceof Error ? error.message : String(error),
              stack: error instanceof Error ? error.stack : undefined
            }
          })
        },
        'CarouselWrapper'
      );
    }
  }

  const currentPathname =
    pathname ||
    (typeof window !== 'undefined' ? window.location.pathname : '');

  const isAdsBannerPage =
    !!adsContext?.landingPath &&
    currentPathname.replace(/\/$/, '') ===
      adsContext.landingPath.replace(/\/$/, '');

  const changeComponentes = (filters?: OptionsFilters) => {
    const { service, service_params } = component;
    let eventsIdsRecents = service_params?.events_ids || '';

    if (component.uuid === 'recentes') {
      const recents = getCardPreviousView()?.recents;
      if (Array.isArray(recents) && recents?.length > 0)
        eventsIdsRecents = recents.join(',');
      if (!eventsIdsRecents || eventsIdsRecents?.length <= 0) return;
    }

    const p = searchParams?.get('p') as string | undefined;

    setIsLoading(true);
    searchCategoryType({
      ...service_params,
      d: discountCode,
      p,
      sort: filters?.sort || service_params?.sort || '',
      range: filters?.range || service_params?.range || '',
      service,
      events_ids: eventsIdsRecents || ''
    })
      .then((response) => {
        const nameData = cardOptions[component.response_type]?.nameData;
        if (nameData) {
          setCardComponentsDataList(response[nameData] || []);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (specialUUIDs.includes(component.uuid) || !component.response) {
      changeComponentes();
    }
  }, [component]);

  useEffect(() => {
    if (!isLoading && inView) {
      const topsortCampaignId = isAdsBannerPage
        ? getCookie('topsort_campaign_id')
        : null;

      analytics.sendEvent(analyticsEvents.rail_event_viewed, {
        item_rail_title: component?.label?.trim() || '',
        item_rail_location: pageTitleForTracking?.trim(),
        item_rail_filter: (filterCarousel.uuid || '')?.replace(/-/g, ' '),
        ...(topsortCampaignId
          ? { item_ads_campaign_id: topsortCampaignId }
          : {})
      });
    }
  }, [isLoading, inView]);

  const Card = cardOptions[component.response_type];
  if (!Card) return null;

  const CardComponent = Card.component;

  const handleLoadCarouselData = useMemo(() => {
    const carouselPayload = CardComponentsDataList.map(
      (item, index: number) => {
        const skipCards: Array<keyof CardOptions> = [
          cardVariants.bannerCard,
          cardVariants.bannerLogin,
          cardVariants.bannerOss
        ];
        const finalCard = component.response_type.replace('-card', '');
        const itemValue = item[Card.urn as keyof typeof item];
        const paramValue =
          Card.urn in item && typeof itemValue !== 'object'
            ? String(itemValue)
            : '';

        const linkUrl = skipCards.includes(component.response_type)
          ? ''
          : createLinkToRedirect({
              param: paramValue,
              routeType:
                finalCard === 'event'
                  ? `event-${'company' in item ? item.company : ''}`
                  : finalCard,
              item: {
                ...item,
                urn: Card.paramsToBuildUrl
                  ? Card.paramsToBuildUrl(
                      filterCarousel?.uuid || component.uuid
                    )
                  : ''
              },
              isDirectory: component.is_directory,
              userLocation,
              serviceParams: { ...component.service_params },
              forwardParams: {
                ...component.forward_params,
                utm_source: utmSource,
                utm_campaign: utmCampaign,
                ...(discountCode && { d: discountCode })
              },
              accountId,
              timestamp,
              hash
            });

        const handleTrackAnalyticsClickRecommendedEvent = (
          event: CollectionData | CollectionMatrix
        ) => {
          return () => {
            const {
              label = '',
              name,
              id,
              url
            } = event as CollectionData & Record<'label', string>;

            try {
              if (
                component.response_type === cardVariants.eventCard &&
                isAdsBannerPage
              ) {
                const resolvedBidId = adsContext?.bidId;
                if (!resolvedBidId) return;

                const isBileto =
                  typeof url === 'string' && url.includes('bileto');

                const biletoId = isBileto
                  ? url.match(/\/event\/(\d+)/)?.[1]
                  : null;

                const productId = biletoId || String(id);

                sendTopsortEvent({
                  type: 'click',
                  resolvedBidId,
                  idBanner: `collection_${index + 1}`,
                  additionalAttribution: { id: productId, type: 'product' }
                });
              }
            } catch (error) {
              logger.error(
                {
                  message:
                    'Unable to get cookie data for Topsort click event or send event',
                  data: JSON.stringify({
                    error: {
                      message:
                        error instanceof Error ? error.message : String(error),
                      stack: error instanceof Error ? error.stack : undefined
                    }
                  })
                },
                'CarouselWrapper'
              );
            }

            const topsortCampaignId = getCookie('topsort_campaign_id');
            const optionalCampaignProps = topsortCampaignId
              ? { item_ads_campaign_id: topsortCampaignId }
              : {};

            const getCollectionCardData = (): AnalyticsCardData => [
              analyticsEvents.collection_clicked,
              {
                collection_title: label.trim(),
                transport: transportMethods.sendBeacon,
                ...optionalCampaignProps
              }
            ];

            const getEventCardData = (): AnalyticsCardData => {
              const baseParams = getParamsRailClicked({
                eventName: name?.trim(),
                eventId: id,
                pageTitleForTracking: pageTitleForTracking?.trim(),
                nameRail: component?.label?.trim(),
                filters: (filterCarousel.uuid || '')?.replace(/-/g, ' ')
              });

              return [
                analyticsEvents.rail_event_clicked,
                {
                  ...baseParams,
                  ...optionalCampaignProps
                }
              ];
            };

            const getOrganizerCardData = (): AnalyticsCardData => [
              analyticsEvents.rail_event_clicked,
              {
                item_rail_city: label.trim(),
                item_rail_location: pageTitleForTracking?.trim(),
                item_rail_title: component?.label?.trim(),
                transport: transportMethods.sendBeacon,
                ...optionalCampaignProps
              }
            ];

            const eventMap = new Map<
              typeof component.response_type,
              AnalyticsCardData
            >([
              ['collection-card', getCollectionCardData()],
              ['event-card', getEventCardData()],
              ['recent-card', getEventCardData()],
              ['city-card', getOrganizerCardData()]
            ]);

            const [eventName, payload] =
              eventMap.get(component.response_type) || [];

            if (eventName) analytics.sendEvent(eventName, payload);
          };
        };

        return {
          slide: (
            <CardComponent
              key={component.uuid}
              href={linkUrl}
              loadingImage={index >= 4 ? 'lazy' : 'eager'}
              {...createPropsToTrackingMatrix(index, {
                trackingPrefix: prefixTrackingName,
                matrizTemplate: matrixUuid || '',
                componentTemplate: component.uuid,
                dataLocation: userLocation,
                cardType: component.response_type,
                cardPreviousView,
                templateType: component.response_type || component.template,
                serviceParams: component.service_params
              })}
              {...item}
              isCollectionSearchVariant={isCollectionSearchVariant}
              url={linkUrl}
              onClick={handleTrackAnalyticsClickRecommendedEvent(item)}
            />
          )
        };
      }
    );

    if (
      component.show_more &&
      Card.urlSeeAll &&
      component.response_type !== cardVariants.bannerCard &&
      component.response_type !== cardVariants.cityCard &&
      CardComponentsDataList.length > Card.initialCardsQty
    ) {
      carouselPayload.push({
        slide: (
          <ShowMoreCard
            key="show-more"
            cardType={component.response_type}
            lang="PT"
            href={createLinkToRedirect({
              param: matrixType,
              routeType: Card.urlSeeAll,
              accountId,
              timestamp,
              hash,
              item: {
                urn: Card.paramsToBuildUrl
                  ? Card.paramsToBuildUrl(
                      filterCarousel?.uuid || component.uuid
                    )
                  : '',
                ordem: component?.service_params?.sort || ''
              },
              forwardParams: {
                ...component.forward_params,
                utm_source: utmSource,
                utm_campaign: utmCampaign,
                ...(discountCode && { d: discountCode })
              },
              isDirectory: component.is_directory,
              userLocation,
              pathname: pathname ?? ''
            })}
          />
        )
      });
    }

    return carouselPayload;
  }, [
    CardComponentsDataList,
    component,
    utmSource,
    utmCampaign,
    accountId,
    timestamp,
    hash,
    discountCode,
    isAdsBannerPage
  ]);

  if (isLoading) {
    return (
      <div
        key={component.uuid}
        id={`section-container-${component.template}-${component.uuid}`}
        data-section-name={`section-container-${component.template}`}
        className={`${styles.sectionContainer} animated fadeIn`}
      >
        <div className={styles.sectionContainerHeader}>
          <h2 className={styles.sectionContainerTitle}>{component.label}</h2>
        </div>
        <div
          className={`${
            styles.sectionContainerCarousel[component.response_type]
          } animated fadeIn`}
        >
          {component.response_type === 'city-card' ? (
            <CityCarouselSkeleton count={5} />
          ) : (
            <CarouselWrapperSkeleton
              untitled
              type={component.response_type}
              numberOfComponents={
                component.response_type === cardVariants.organizerCard ? 2 : 4
              }
            />
          )}
        </div>
      </div>
    );
  }

  if (CardComponentsDataList.length <= 0) return null;

  const handleBlur = (event: React.FocusEvent) => {
    if (
      authContainerRef.current &&
      !authContainerRef.current.contains(event.relatedTarget as Node)
    ) {
      setIsDropdownOpen(false);
    }
  };

  const getTitle = () => {
    if (hideTitle) {
      return null;
    }

    if (filterMatrix) {
      const optionsDropdown = filterMatrix?.map(({ title, ...filters }) => {
        const selected = title === filterCarousel.title;
        const getEvents = () => {
          if (!selected) {
            setIsDropdownOpen(false);
            setFilterCarousel({ title, ...filters });
            changeComponentes(filters.optionsFilters);
          }
          addEventToDataLayer({
            category: FILTER_CAROUSEL,
            label: `${PAGE}${matrixUuid}.${component?.uuid}:${title}`
          });
        };
        return {
          link: getEvents,
          label: filters?.label ?? title,
          selected
        };
      });

      return (
        <div className={styles.sectionContainerFilter}>
          {component?.label && (
            <h2 className={styles.sectionContainerTitle}>{component.label}</h2>
          )}
          <div className={styles.dropdownContainer} ref={authContainerRef} />

          <button
            type="button"
            onBlur={handleBlur}
            className={styles.sectionContentFilter}
            aria-label="Mudar cidade"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          >
            <Dropdown
              topDistance="20px"
              isOpen={isDropdownOpen}
              options={optionsDropdown}
              container={authContainerRef.current}
              onOpenChange={() => {
                setIsDropdownOpen(true);
              }}
              side="bottom"
              trigger={<div className={styles.dropdownTrigger} />}
            />

            <p className={styles.textFilter}>{filterCarousel?.title}</p>
            <IconArrowDown width={24} height={24} />
          </button>
        </div>
      );
    }

    return (
      component.label && (
        <h2 className={styles.sectionContainerTitle}>{component.label}</h2>
      )
    );
  };

  return (
    <div
      key={component.uuid}
      id={`section-container-${component.template}-${component.uuid}`}
      data-section-name={`section-container-${component.template}`}
      className={`${styles.sectionContainer} animated fadeIn`}
    >
      <div className={styles.sectionContainerHeader}>
        {getTitle()}
        {CardComponentsDataList.length > Card.initialCardsQty && !hideTitle && (
          <div className={styles.sectionContainerButtons}>
            <div
              className={styles.sectionContainerButtonSeeAll}
              style={{ display: component.show_more ? 'flex' : 'none' }}
            >
              <a
                className={styles.sectionContainerButtonLink}
                href={createLinkToRedirect({
                  param: matrixType,
                  routeType: Card.urlSeeAll || '',
                  accountId,
                  timestamp,
                  hash,
                  item: {
                    urn: Card.paramsToBuildUrl
                      ? Card.paramsToBuildUrl(
                          filterCarousel?.uuid || component.uuid
                        )
                      : '',
                    ordem: component?.service_params?.sort || ''
                  },
                  forwardParams: {
                    ...component.forward_params,
                    utm_source: utmSource,
                    utm_campaign: utmCampaign,
                    ...(discountCode && { d: discountCode })
                  },
                  isDirectory: component.is_directory,
                  userLocation,
                  pathname: pathname ?? ''
                })}
                onClick={() =>
                  analytics.sendEvent(analyticsEvents.rail_view_more_clicked, {
                    item_rail_title: component?.label?.trim(),
                    transport: transportMethods.sendBeacon,
                    item_rail_filter: (filterCarousel.uuid || '')?.replace(
                      /-/g,
                      ' '
                    )
                  })
                }
              >
                Ver tudo
              </a>
            </div>
          </div>
        )}
      </div>

      <div
        className={
          isCollectionSearchVariant &&
          CardComponentsDataList.length > Card.initialCardsQty
            ? styles.navigationContainer
            : styles.navigationContainerNormal
        }
      >
        {isCollectionSearchVariant &&
          CardComponentsDataList.length > Card.initialCardsQty && (
            <Button
              variant="text"
              icon={<IconArrowLeft width={16} height={16} />}
              onClick={controls.prev}
              disabled={btnArrows.beginning}
              tabIndex={-1}
              aria-label="Previous"
              className={
                isCollectionSearchVariant
                  ? styles.sectionContainerButton.base
                  : styles.sectionContainerButton.hidden
              }
            />
          )}
      </div>
      <div
        ref={ref}
        className={`${styles.sectionContainerCarousel[component.response_type]}
          ${
            isCollectionSearchVariant &&
            CardComponentsDataList.length > Card.initialCardsQty
              ? styles.carouselFlexContainer
              : ''
          } animated fadeIn`}
      >
        {CardComponentsDataList.length > Card.initialCardsQty && (
          <div className={styles.sectionContainerButtonsIcons}>
            <Button
              variant="text"
              icon={<IconArrowLeft width={20} height={20} />}
              onClick={controls.prev}
              disabled={btnArrows.beginning}
              tabIndex={-1}
              aria-label="Previous"
              className={`
                ${styles.sectionContainerButton.base} ${
                  styles.sectionContainerButton.prev
                }
                ${
                  component?.response_type === cardVariants.eventCard ||
                  component?.response_type === cardVariants.recentCard
                    ? styles.sectionContainerButton.cardButtonBase
                    : styles.sectionContainerButton.arrowButtonBase
                }
              `}
            />
            <Button
              variant="text"
              icon={<IconArrowRight width={20} height={20} />}
              onClick={controls.next}
              disabled={btnArrows.end}
              tabIndex={-1}
              aria-label="Next"
              className={`
                ${styles.sectionContainerButton.base} ${
                  styles.sectionContainerButton.next
                }
                ${
                  component?.response_type === cardVariants.eventCard ||
                  component?.response_type === cardVariants.recentCard
                    ? styles.sectionContainerButton.cardButtonBase
                    : styles.sectionContainerButton.arrowButtonBase
                }
              `}
            />
          </div>
        )}
        {!isMounted || !hasData ? (
          component.response_type === 'city-card' ? (
            <CityCarouselSkeleton />
          ) : (
            <CarouselWrapperSkeleton
              untitled
              type={component.response_type}
              numberOfComponents={
                component.response_type === cardVariants.organizerCard
                  ? 2
                  : component.response_type === cardVariants.collectionCard
                    ? 8
                    : 4
              }
            />
          )
        ) : (
          <>
            {component.response_type === 'city-card' ? (
              <CityCarousel
                options={handleLoadCarouselData}
                controls={(payload) => setControls({ ...controls, ...payload })}
                updateArrows={(payload) => setBtnArrows(payload)}
              />
            ) : (
              <Carousel
                id={`${component.order}`}
                key={component.order}
                type={component.response_type}
                options={handleLoadCarouselData}
                controls={(payload) => setControls({ ...controls, ...payload })}
                updateArrows={(payload) => setBtnArrows(payload)}
              />
            )}
          </>
        )}
      </div>

      <div
        className={
          isCollectionSearchVariant &&
          CardComponentsDataList.length > Card.initialCardsQty
            ? styles.navigationContainer
            : styles.navigationContainerNormal
        }
      >
        {isCollectionSearchVariant &&
          CardComponentsDataList.length > Card.initialCardsQty && (
            <Button
              variant="text"
              icon={<IconArrowRight width={16} height={16} />}
              onClick={controls.next}
              disabled={btnArrows.end}
              tabIndex={-1}
              aria-label="Next"
              className={
                isCollectionSearchVariant
                  ? styles.sectionContainerButton.base
                  : styles.sectionContainerButton.hidden
              }
            />
          )}
      </div>
    </div>
  );
}
